import { makeStyles } from '@material-ui/styles'
import TenantRolesTableField from 'account/components/userManagement/users/TenantRolesTableField'
import UserPasswordField from 'account/components/userManagement/users/UserPasswordField'
import Progress from 'core/components/progress/Progress'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import { emailValidator, requiredValidator } from 'core/utils/fieldValidators'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useEffect, useState } from 'react'
import useReactRouter from 'use-react-router'
import DomainPickList from '../domains/DomainPickList'
import useTenantSelector from '../tenants/useTenantSelector'
import { createUser } from './new-actions'
import { isSystemAdminUser } from 'core/utils/helpers'
import DomainNameTextField from '../domains/DomainNameTextField'

interface Props {
  addRoute: Route
  domainId: string
}

const tenantRolesValidations = [requiredValidator.withMessage('Must select at least one tenant')]

export const accountActivationRadioOptions = [
  {
    value: 'activationByEmail',
    label: 'Send activation email to the user.',
    info:
      ' Instructions to create a new password and to activate account will be sent to the email provided.',
  },
  {
    value: 'createPassword',
    label: 'Set password for new user now.',
    info: 'Create password for the new user now and activate the account immediately.',
  },
]

export default function CreateUserModal({ addRoute, domainId }: Props) {
  const defaultParams = {
    username: '',
    displayname: '',
    password: '',
    roleAssignments: {},
    domainId: domainId,
  }
  const { history } = useReactRouter()
  const [activationType, setActivationType] = useState('createPassword')
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { update, updating, error, reset } = useUpdateAction(createUser)

  const { data: tenants, loading: loadingTenants } = useTenantSelector(params.domainId)

  // Update domainId in params
  useEffect(() => {
    setParams({ ...params, domainId })
  }, [domainId])

  const submitForm = useCallback(async () => {
    const body = {
      username: params.username,
      displayname: params.displayname,
      password: params.password,
      roleAssignments: params.roleAssignments,
      activationType: activationType,
      domain_id: params.domainId,
    }
    const { success } = await update(body)
    if (success) {
      handleClose()
    }
  }, [params, activationType])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    history.push(routeManager.routes.userManagement.users.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create User`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={error}
      initialValues={defaultParams}
      submitTitle={`Create User`}
    >
      <>
        <FormFieldSection title="User Settings">
          <TextField
            id="username"
            label="Email"
            onChange={getParamsUpdater('username')}
            value={params.username}
            validations={[emailValidator]}
            required
          />
          <TextField
            id="displayname"
            label="Display Name"
            onChange={getParamsUpdater('displayname')}
            value={params.displayname}
          />
          {activationType === 'createPassword' && (
            <UserPasswordField value={params.password} onChange={getParamsUpdater('password')} />
          )}
          {isSystemAdminUser() ? (
            <DomainPickList
              onChange={getParamsUpdater('domainId')}
              value={params.domainId}
              required
            />
          ) : (
            <DomainNameTextField domainId={params.domainId} isReadOnly />
          )}
        </FormFieldSection>
        <FormFieldSection title="Tenant Assignment">
          <Progress
            renderContentOnMount={!loadingTenants}
            loading={loadingTenants}
            message={'Loading Tenants...'}
          >
            <TenantRolesTableField
              validations={tenantRolesValidations}
              id="roleAssignments"
              tenants={tenants}
              onChange={getParamsUpdater('roleAssignments')}
            />
          </Progress>
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
